<script setup>
const { response, helper, t } = useConstructor();
const emit = defineEmits(['update:modelValue']);

const props = defineProps({
	autocomplete: {
		type: [Boolean, String],
		default: 'off',
	},
	visible: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	required: {
		type: Boolean,
		default: false,
	},
	readonly: {
		type: Boolean,
		default: false,
	},
	label: {
		type: String,
		default: null,
	},
	noLabel: {
		type: Boolean,
		default: false,
	},
	step: {
		type: [String, Number],
		default: null,
	},
	maxlength: {
		type: [String, Number],
		default: null,
	},
	min: {
		type: [String, Number],
		default: null,
	},
	max: {
		type: [String, Number],
		default: null,
	},
	modelValue: {
		type: [String, Object, Number, Date],
		default: null,
	},
	name: {
		type: String,
		required: true,
	},
	placeholder: {
		type: String,
		default: null,
	},
	options: {
		type: Array,
		default: () => [],
	},
	type: {
		type: String,
		default: 'text',
	},
	iconRight: {
		type: String,
		default: null,
	},
	iconLeft: {
		type: String,
		default: null,
	},
	iconRightColor: {
		type: String,
		default: null,
	},
	iconLeftColor: {
		type: String,
		default: null,
	},
	size: {
		type: String,
		default: 'small',
	},
});

const typeElement = ref(props.type);
// Methods
const getDecimalLength = (number) => {
	if (typeof number !== 'number') {
		throw new Error('Input must be a number');
	}
	const decimalPart = (number % 1).toString().split('.')[1];
	return decimalPart ? decimalPart.length : 0;
};

const changeType = () => {
	typeElement.value = typeElement.value === 'password' ? 'text' : 'password';
};

// Computed
const formSize = computed(() => {
	switch (props.size) {
		case 'small':
			return 'uk-form-small';
		case 'medium':
			return 'uk-form-medium';
		case 'large':
			return 'uk-form-large';
		default:
			return 'uk-form-small';
	}
});

const _label = computed(() => {
	if (props.label) {
		return props.label;
	} else if (props.name) {
		return t(helper.headline(props.name));
	} else {
		return null;
	}
});

const _placeholder = computed(() => {
	if (props.placeholder) {
		return props.placeholder;
	} else if (_label.value !== null) {
		return t('Please Fill') + ' ' + t(helper.headline(_label.value));
	} else {
		return t('Please Fill Data . . .');
	}
});

const decimal = computed(() => {
	return props.step ? getDecimalLength(parseFloat(props.step)) : 0;
});

const viewValue = computed(() => {
	if (!props.modelValue) return '-';
	if (props.type === 'number') {
		if (decimal.value > 0) {
			return parseFloat(props.modelValue).toFixed(decimal.value);
		} else {
			return helper.numberFormat(props.modelValue);
		}
	} else if (props.type === 'password') {
		return '•'.repeat(props.modelValue.length);
	}
	return props.modelValue;
});

// Watchers
watch(
	() => props.modelValue,
	(value) => {
		if (value) {
			if (response.errors?.[props.name]) delete response.errors[props.name];
			if (props.name?.toLowerCase() === 'code') {
				emit('update:modelValue', value.toUpperCase());
			}
		}
	},
);
</script>


<template>
	<div>
		<template v-if="_label && !noLabel">
			<div v-if="disabled" class="uk-form-label uk-text-capitalize">
				{{ _label }}
			</div>

			<label v-else class="uk-form-label  uk-text-capitalize" :for="name">
				{{ _label }}
				<span v-if="required" class="uk-text-danger">(*)</span>

				<div v-if="!response.hasResponse" uk-spinner="ratio: 0.7" class="uk-float-right" />
			</label>
		</template>

		<div class="uk-form-controls uk-inline uk-width-1-1">
			<span v-if="iconLeft" class="uk-form-icon">
				<component :is="iconLeft" />
			</span>

			<input v-if="!disabled" :id="name" :name="name" class="uk-input" :type="typeElement"
				:placeholder="_placeholder" :value="modelValue" :required="required" :readonly="readonly"
				:maxlength="maxlength" :autocomplete="autocomplete"
				:class="[formSize, { 'uk-form-danger': response.errors && response.errors[name], 'uk-text-right': type == 'number' }]"
				:step="step" :min="min" :max="max" :disabled="!response.hasResponse"
				@input="$emit('update:modelValue', $event.target.value)" @focus="$event.target.select()">

			<p v-else-if="type == 'color'" class="uk-margin-remove">
				<span class="uk-badge uk-margin-small-right uk-width-small" :style="{ 'background-color': modelValue }"
					uk-badge />
			</p>

			<div v-else class="uk-margin-remove uk-padding-remove uk-text-muted" :class="[
				formSize,
				{
					'uk-text-right': type == 'number',
					'no-border': !noLabel
				}
			]">
				{{ helper.formatIfDateTime(viewValue) }}
			</div>

			<span v-if="iconRight" class="uk-form-icon uk-form-icon-flip" :style="visible ? 'margin-right:25px;' : ''">
				<component :is="iconRight" :color="iconRightColor" />
			</span>

			<a v-if="visible && !disabled" class="uk-form-icon uk-form-icon-flip" @click="changeType">
				<EyeOffIcon v-if="typeElement == 'password'" />

				<EyeIcon v-else />
			</a>

			<template v-if="response.errors && response.errors[name]">
				<span v-if="type != 'number'" class="uk-form-icon uk-form-icon-flip"
					:style="visible ? 'margin-right:25px;' : ''">
					<AlertCircleIcon color="red" />
				</span>

				<span v-else class="uk-form-icon">
					<AlertCircleIcon color="red" />
				</span>
			</template>
		</div>

		<div v-if="response.errors && response.errors[name]">
			<div class="uk-link-reset uk-text-danger uk-text-small uk-margin-small-top"
				:style="visible ? 'margin-right:25px;' : ''">
				<ul>
					<li v-for="error in response.errors[name]" class="uk-text-left">
						{{ error }}
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>
